import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import { useUpdateStagedSelectionMutation } from '@/state/mutations/player/use-update-staged-selection';
import { useCueCommandMutation } from '@/state/mutations/player/use-cue-command';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import {
  buildPlayedItemIdsSet,
  collectOnAirPreviewItemIds,
  cuePhaseToRowState,
  isInsertOverrideTakeItem,
  isScopeIdleCueItem,
  matchesLiveCueCurrentStep,
  previewItemIdMatches,
  resolveCueItemState,
  isCueAsideResumeRow,
  resolveAsideListChrome,
  resolveLiveScopedCueItemPhase,
  toggleMainContentInSelection,
  type CueItemResolveContext,
} from '@/helpers/perform-staging.helper';
import { useLiveSlideChromeSuppressStore } from '@/state/stores/use-live-slide-chrome-suppress.store';
import type { SimulationCuePlaylistContext } from '@/helpers/simulation-cue-engine.helper';
import { findSimulationRowIndex, resolveScopeIdlePlayItemId } from '@/helpers/simulation-cue-engine.helper';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';
import { isMediaAgentBackupSlidesActive } from '@/helpers/media-agent-recovery.helper';
import type { CueItemPhase } from '@/types/player/perform-control-v1.types';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import {
  isNextCuePreviewTarget,
  resolveCurrentCueSheetPointer,
  resolveLiveNextCueTarget,
  resolveNextCuePreviewRow,
} from '@/helpers/resolve-next-cue-preview-row.helper';
import {
  resolveStartSlideIndexForTake,
  seedLiveSlideIndexForTake,
} from '@/helpers/resolve-start-slide-index-for-take.helper';
import { liveCueSheetToSimulationRows } from '@/helpers/live-cue-sheet-to-simulation-rows.helper';
import { usePerformAdvanceCue } from '@/hooks/use-perform-advance-cue';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { entityRoomKeyFromEntityRef, usePerformControlWsStore } from '@/state/player/perform-control-ws.store';
import { useLiveContextStateWithLivePlayState } from '@/hooks/use-live-play-state';
import { useNextCueRecalculatingStore } from '@/state/player/next-cue-recalculating.store';
import {
  isCueListItemIndicatorPending,
  isJumpNextIndicatorReady,
} from '@/helpers/resolve-next-cue-recalculating.helper';

type FlowControlStagingContextValue = {
  entityRef?: string;
  canInteract: boolean;
  usesAutoCue: boolean;
  mediaAgentAvailable?: boolean;
  /** True while the presentation computer is out and slides come from backup renditions. */
  backupSlidesActive: boolean;
  isStaged: (itemId: string, playlistContext?: SimulationCuePlaylistContext) => boolean;
  isPlayed: (itemId: string, playlistContext?: SimulationCuePlaylistContext) => boolean;
  getItemPhase: (itemId: string, playlistContext?: SimulationCuePlaylistContext) => CueItemPhase | undefined;
  isShowingScopeIdle: (itemId: string, playlistContext?: SimulationCuePlaylistContext) => boolean;
  /** True when this row is the cue target shown in the Next preview. */
  isNextCueItem: (itemId: string, playlistContext?: SimulationCuePlaylistContext) => boolean;
  isSkipped: (itemId: string) => boolean;
  toggleItem: (itemId: string, playlistContext?: SimulationCuePlaylistContext) => void;
  /** Same take as transport Next — used when the operator plays the already-highlighted Next row. */
  advanceNextCue: () => void;
  /** Live auto-cue play with explicit jump vs insert mode (from confirmation modal). */
  playCueItem: (
    itemId: string,
    cuePlayMode: 'jump' | 'insert',
    playlistContext?: SimulationCuePlaylistContext,
    /** Captured from item PreviewModal so confirmation cannot lose the operator slide pick. */
    startSlideIndex?: number,
  ) => void;
  setItemSkipped: (itemId: string, skipped: boolean) => void;
  isItemCueCommandPending: (itemId: string, playlistContext?: SimulationCuePlaylistContext) => boolean;
  /** Rundown Current chrome (red border) — includes Play once / jump take targets before phase is on_air. */
  isLiveCurrentItem: (itemId: string, playlistContext?: SimulationCuePlaylistContext) => boolean;
  /**
   * Apply per-item Next show-mode preselect to the Next preview (sim store or staged selection).
   * `inherit` copies the currently on-air show mode. No-op unless `itemId` is the current Next cue target.
   */
  applyNextShowModePreselect: (
    mode: 'inherit' | 'mediaOnly' | 'both' | 'speakerOnly',
    itemId: string,
    playlistContext?: SimulationCuePlaylistContext,
  ) => void;
};

const FlowControlStagingContext = createContext<FlowControlStagingContextValue | null>(null);

export function FlowControlStagingProvider({
  entityRef,
  liveContextState: liveContextStateFromGet,
  liveContextQueryUpdatedAt,
  liveReady,
  children,
}: {
  entityRef?: string;
  liveContextState?: LiveContextStateDto;
  liveContextQueryUpdatedAt?: number;
  liveReady: boolean;
  children: ReactNode;
}) {
  const liveContextState = useLiveContextStateWithLivePlayState(entityRef, liveContextStateFromGet);
  const flow = useFlowStore((s) => s.flow);
  const flowPlan = useFlowStore((s) => s.flowPlan);
  const flowToUse = flowPlan ?? flow;
  const performMode = flowToUse?.liveContext?.isRunning ? 'play' : 'simulation';
  const simulationPointer = useFlowControlSimulationCueStore((s) => s.pointer);
  const simulationOverridePointer = useFlowControlSimulationCueStore((s) => s.overridePointer);
  const simulationRows = useFlowControlSimulationCueStore((s) => s.rows);
  const simulationSelectedItemIds = useFlowControlSimulationCueStore((s) => s.selectedItemIds);
  const playedReturnPreviousKeys = useFlowControlSimulationCueStore((s) => s.playedReturnPreviousKeys);
  const simulationNextShowMode = useFlowControlSimulationCueStore((s) => s.nextShowMode);
  const simulationOnAirShowMode = useFlowControlSimulationCueStore((s) => s.onAirShowMode);
  const setSimulationNextShowMode = useFlowControlSimulationCueStore((s) => s.setNextShowMode);
  const clearNextShowModePreselectHold = useFlowControlSimulationCueStore((s) => s.clearNextShowModePreselectHold);
  const simulationPlayMedia = useFlowControlSimulationCueStore((s) => s.playMedia);
  const simulationStopMedia = useFlowControlSimulationCueStore((s) => s.stopMedia);
  const { hasAccess: hasAutoCue } = useFeatureAccess('perform_auto_cue');
  const updateStagedSelection = useUpdateStagedSelectionMutation();
  const cueCommand = useCueCommandMutation();
  const cueState = useLiveContextCueState(entityRef, undefined, liveContextState?.cueState);
  const usesAutoCue = performMode === 'simulation' ? simulationRows.length > 0 : hasAutoCue && !!cueState;
  const effectiveLiveReady = performMode === 'simulation' || liveReady;
  const canInteract = performMode === 'simulation' ? simulationRows.length > 0 : effectiveLiveReady && !!entityRef;
  const { advanceCue } = usePerformAdvanceCue({
    flow: (flowToUse ?? {}) as FlowDto,
    entityRef: entityRef ?? '',
    performMode,
    liveContextState,
    liveContextQueryUpdatedAt,
  });
  const advanceNextCue = useCallback(() => {
    if (!canInteract || !flowToUse) {
      return;
    }
    if (performMode === 'play' && !entityRef) {
      return;
    }
    advanceCue();
  }, [advanceCue, canInteract, entityRef, flowToUse, performMode]);

  const jumpIndicatorLatch = useNextCueRecalculatingStore((s) =>
    entityRef ? (s.latchByEntityRef[entityRef] ?? null) : null,
  );
  const isItemCueCommandPending = useCallback(
    (itemId: string, playlistContext?: SimulationCuePlaylistContext) => {
      if (
        isCueListItemIndicatorPending({
          latch: jumpIndicatorLatch,
          cueState,
          itemId,
          playlistContext,
        })
      ) {
        return true;
      }
      if (!cueCommand.isPending) {
        return false;
      }
      const variables = cueCommand.variables;
      if (!variables || (variables.action !== 'playMedia' && variables.action !== 'stopMedia')) {
        return false;
      }
      return isNextCuePreviewTarget(
        {
          itemId: variables.itemId,
          cueItemId: variables.cueItemId,
          scope: variables.scope,
          slotId: variables.slotId,
        },
        itemId,
        playlistContext,
      );
    },
    [cueCommand.isPending, cueCommand.variables, cueState, jumpIndicatorLatch],
  );

  const chromeTakeTargetItemId = useLiveSlideChromeSuppressStore((s) =>
    entityRef ? s.byEntityRef[entityRef]?.toItemId : undefined,
  );
  const playedItemIds = useMemo(() => {
    if (performMode === 'simulation') {
      return new Set(simulationSelectedItemIds);
    }
    return buildPlayedItemIdsSet(liveContextState);
  }, [liveContextState, performMode, simulationSelectedItemIds]);
  const isLiveCurrentItem = useCallback(
    (itemId: string, playlistContext?: SimulationCuePlaylistContext) => {
      if (performMode === 'simulation') {
        return simulationSelectedItemIds.includes(itemId);
      }
      const asideChrome = resolveAsideListChrome(
        cueState,
        itemId,
        playlistContext,
        liveContextState?.playState?.lookAheadInsertResume,
      );
      if (asideChrome === 'playing') {
        return true;
      }
      if (asideChrome === 'next' || asideChrome === 'other') {
        return false;
      }
      if (cueState?.cueOverrideActive) {
        if (
          isCueAsideResumeRow(cueState, itemId, playlistContext) ||
          isNextCuePreviewTarget(
            resolveLiveNextCueTarget(cueState, liveContextState?.playState?.lookAheadInsertResume),
            itemId,
            playlistContext,
          ) ||
          (!itemId.startsWith('media:') &&
            !matchesLiveCueCurrentStep(cueState, itemId, {
              scope: playlistContext?.scope,
              slotId: playlistContext?.slotId,
              cueItemId: playlistContext?.cueItemId,
            }))
        ) {
          return false;
        }
      }
      if (
        cueState &&
        matchesLiveCueCurrentStep(cueState, itemId, {
          scope: playlistContext?.scope,
          slotId: playlistContext?.slotId,
          cueItemId: playlistContext?.cueItemId,
        })
      ) {
        return true;
      }
      if (isItemCueCommandPending(itemId, playlistContext)) {
        return true;
      }
      if (
        chromeTakeTargetItemId &&
        isNextCuePreviewTarget({ itemId: chromeTakeTargetItemId }, itemId, playlistContext)
      ) {
        return true;
      }
      const jumpTarget = jumpIndicatorLatch?.targetItemId;
      if (
        jumpTarget &&
        isNextCuePreviewTarget(
          {
            itemId: jumpTarget,
            cueItemId: jumpIndicatorLatch?.targetContext?.cueItemId,
            scope: jumpIndicatorLatch?.targetContext?.scope,
            slotId: jumpIndicatorLatch?.targetContext?.slotId,
          },
          itemId,
          playlistContext,
        )
      ) {
        return true;
      }
      // Insert / play-once: Current preview is already the aside while cue currentStep can
      // still point at the auto/resume row. Key chrome off Current layers, not historic played.
      const insertOverlay =
        cueState?.cueOverrideActive === true || Boolean(liveContextState?.playState?.lookAheadInsertResume?.itemId);
      if (
        insertOverlay &&
        usesAutoCue &&
        performMode === 'play' &&
        isInsertOverrideTakeItem(itemId) &&
        previewItemIdMatches(itemId, collectOnAirPreviewItemIds(liveContextState)) &&
        !isNextCuePreviewTarget(
          resolveLiveNextCueTarget(cueState, liveContextState?.playState?.lookAheadInsertResume),
          itemId,
          playlistContext,
        )
      ) {
        return true;
      }
      return false;
    },
    [
      chromeTakeTargetItemId,
      cueState,
      isItemCueCommandPending,
      jumpIndicatorLatch,
      liveContextState,
      performMode,
      simulationSelectedItemIds,
      usesAutoCue,
    ],
  );

  const selectedItemIds = useMemo(() => {
    if (performMode === 'simulation') {
      return simulationSelectedItemIds;
    }
    return liveContextState?.stagedSelection?.selectedItemIds ?? [];
  }, [liveContextState?.stagedSelection?.selectedItemIds, performMode, simulationSelectedItemIds]);
  const skippedItemIds = useMemo(
    () => liveContextState?.stagedSelection?.skippedItemIds ?? [],
    [liveContextState?.stagedSelection?.skippedItemIds],
  );
  const rawShowMode =
    performMode === 'simulation'
      ? simulationNextShowMode
      : (liveContextState?.stagedSelection?.showMode ?? 'mediaOnly');
  const showMode: 'mediaOnly' | 'both' | 'speakerOnly' = rawShowMode === 'mixedView' ? 'both' : rawShowMode;

  const cueItems = cueState?.items;

  const resolveSimulationOrdinal = useCallback(
    (itemId: string, playlistContext?: SimulationCuePlaylistContext) =>
      findSimulationRowIndex(simulationRows, itemId, playlistContext, simulationPointer),
    [simulationPointer, simulationRows],
  );

  const toCueResolveContext = useCallback(
    (playlistContext?: SimulationCuePlaylistContext): CueItemResolveContext | undefined => {
      if (!playlistContext?.scope && !playlistContext?.cueItemId) {
        return undefined;
      }
      return {
        scope: playlistContext?.scope,
        slotId: playlistContext?.slotId,
        cueItemId: playlistContext?.cueItemId,
      };
    },
    [],
  );

  const resolveScopedCuePhase = useCallback(
    (itemId: string, playlistContext?: SimulationCuePlaylistContext): CueItemPhase | undefined => {
      const resolveContext = toCueResolveContext(playlistContext);
      return resolveLiveScopedCueItemPhase(cueState, cueItems, itemId, resolveContext);
    },
    [cueItems, cueState, toCueResolveContext],
  );

  const getItemPhase = useCallback(
    (itemId: string, playlistContext?: SimulationCuePlaylistContext): CueItemPhase | undefined => {
      if (performMode === 'simulation') {
        if (simulationSelectedItemIds.includes(itemId)) {
          return 'on_air';
        }
        const ordinal = resolveSimulationOrdinal(itemId, playlistContext);
        if (ordinal === simulationPointer && ordinal >= 0) {
          const row = simulationRows[ordinal];
          if (row?.itemId === itemId) {
            const scopeIdleId = resolveScopeIdlePlayItemId(simulationRows, ordinal);
            if (scopeIdleId && simulationSelectedItemIds.includes(scopeIdleId)) {
              return 'idle';
            }
          }
        }
        return 'idle';
      }
      const phase = resolveScopedCuePhase(itemId, playlistContext);
      const chrome = resolveAsideListChrome(
        cueState,
        itemId,
        toCueResolveContext(playlistContext),
        liveContextState?.playState?.lookAheadInsertResume,
      );
      if (chrome === 'playing') {
        return 'on_air';
      }
      if (chrome === 'next') {
        return phase === 'on_air' || phase == null ? 'preloaded' : phase;
      }
      if (chrome === 'other' && phase === 'on_air') {
        return itemId.startsWith('media:') ? 'played' : 'idle';
      }
      return phase;
    },
    [
      cueState,
      liveContextState?.playState?.lookAheadInsertResume,
      performMode,
      resolveScopedCuePhase,
      resolveSimulationOrdinal,
      simulationPointer,
      simulationRows,
      simulationSelectedItemIds,
      toCueResolveContext,
    ],
  );

  const isShowingScopeIdle = useCallback(
    (itemId: string, playlistContext?: SimulationCuePlaylistContext): boolean => {
      if (performMode === 'simulation') {
        const ordinal = resolveSimulationOrdinal(itemId, playlistContext);
        if (ordinal !== simulationPointer || ordinal < 0) {
          return false;
        }
        const row = simulationRows[ordinal];
        if (!row || row.itemId !== itemId) {
          return false;
        }
        const scopeIdleId = resolveScopeIdlePlayItemId(simulationRows, ordinal);
        return Boolean(
          scopeIdleId && simulationSelectedItemIds.includes(scopeIdleId) && !simulationSelectedItemIds.includes(itemId),
        );
      }
      if (cueState?.cueOverrideActive) {
        return false;
      }
      const entry = resolveCueItemState(cueItems, itemId, toCueResolveContext(playlistContext));
      return isScopeIdleCueItem(entry);
    },
    [
      cueItems,
      cueState?.cueOverrideActive,
      performMode,
      resolveSimulationOrdinal,
      simulationPointer,
      simulationRows,
      simulationSelectedItemIds,
      toCueResolveContext,
    ],
  );

  const isStaged = useCallback(
    (itemId: string, playlistContext?: SimulationCuePlaylistContext) => {
      if (performMode === 'simulation') {
        const phase = getItemPhase(itemId, playlistContext);
        return cuePhaseToRowState(phase).staged;
      }
      if (usesAutoCue) {
        return cuePhaseToRowState(resolveScopedCuePhase(itemId, playlistContext)).staged;
      }
      return selectedItemIds.includes(itemId);
    },
    [cueItems, getItemPhase, performMode, selectedItemIds, resolveScopedCuePhase, usesAutoCue],
  );

  const isPlayed = useCallback(
    (itemId: string, playlistContext?: SimulationCuePlaylistContext) => {
      if (performMode === 'simulation') {
        const phase = getItemPhase(itemId, playlistContext);
        return cuePhaseToRowState(phase).played;
      }
      if (usesAutoCue) {
        return cuePhaseToRowState(resolveScopedCuePhase(itemId, playlistContext)).played;
      }
      return playedItemIds.has(itemId);
    },
    [cueItems, getItemPhase, performMode, playedItemIds, resolveScopedCuePhase, usesAutoCue],
  );

  const isSkipped = useCallback(
    (itemId: string) => skippedItemIds.includes(itemId) || resolveCueItemState(cueItems, itemId)?.phase === 'skipped',
    [cueItems, skippedItemIds],
  );

  const setItemSkipped = useCallback(
    (itemId: string, skipped: boolean) => {
      if (!entityRef || !canInteract) {
        return;
      }
      const isSkippedNow = skippedItemIds.includes(itemId);
      if (skipped === isSkippedNow) {
        return;
      }
      const nextSkipped = skipped ? [...skippedItemIds, itemId] : skippedItemIds.filter((id) => id !== itemId);
      updateStagedSelection.mutate({
        entityRef,
        stagedSelection: { selectedItemIds, showMode, skippedItemIds: nextSkipped },
      });
    },
    [canInteract, entityRef, selectedItemIds, showMode, skippedItemIds, updateStagedSelection],
  );

  const buildLiveCueCommandContext = useCallback(
    (itemId: string, playlistContext?: SimulationCuePlaylistContext) => {
      const resolveContext = toCueResolveContext(playlistContext);
      // session-info / break-info repeat per slot — never send an ambiguous first-match cueItemId.
      const needsSlot =
        itemId === 'composite:session-info' ||
        itemId === 'composite:break-info' ||
        resolveContext?.scope === 'session' ||
        resolveContext?.scope === 'slot';
      if (needsSlot && !resolveContext?.slotId) {
        return null;
      }
      const cueEntry = resolveCueItemState(cueItems, itemId, resolveContext);
      return {
        cueEntry,
        commandContext: {
          ...(cueEntry?.cueItemId ? { cueItemId: cueEntry.cueItemId } : {}),
          ...(resolveContext?.scope ? { scope: resolveContext.scope } : {}),
          ...(resolveContext?.slotId ? { slotId: resolveContext.slotId } : {}),
        },
      };
    },
    [cueItems, toCueResolveContext],
  );

  const playCueItem = useCallback(
    (
      itemId: string,
      cuePlayMode: 'jump' | 'insert',
      playlistContext?: SimulationCuePlaylistContext,
      capturedStartSlideIndex?: number,
    ) => {
      if (!canInteract) return;

      if (performMode === 'simulation') {
        simulationPlayMedia(itemId, playlistContext, cuePlayMode);
        return;
      }

      if (!entityRef || !usesAutoCue) return;
      const built = buildLiveCueCommandContext(itemId, playlistContext);
      if (!built) return;
      const resolved = resolveStartSlideIndexForTake(itemId, { playlistContext });
      const startSlideIndex =
        capturedStartSlideIndex != null && capturedStartSlideIndex > 0 ? capturedStartSlideIndex : resolved;
      seedLiveSlideIndexForTake(itemId, startSlideIndex);
      cueCommand.mutate({
        action: 'playMedia',
        entityRef,
        itemId,
        cuePlayMode,
        ...built.commandContext,
        ...(startSlideIndex != null ? { startSlideIndex } : {}),
      });
    },
    [buildLiveCueCommandContext, canInteract, cueCommand, entityRef, performMode, simulationPlayMedia, usesAutoCue],
  );

  const toggleItem = useCallback(
    (itemId: string, playlistContext?: SimulationCuePlaylistContext) => {
      if (!canInteract) return;

      if (performMode === 'simulation') {
        const phase = getItemPhase(itemId, playlistContext);
        if (phase === 'on_air') {
          simulationStopMedia(itemId, playlistContext);
        } else {
          simulationPlayMedia(itemId, playlistContext);
        }
        return;
      }

      if (!entityRef) return;

      if (usesAutoCue) {
        const built = buildLiveCueCommandContext(itemId, playlistContext);
        if (!built) return;
        if (built.cueEntry?.phase === 'on_air') {
          cueCommand.mutate({ action: 'stopMedia', entityRef, itemId, ...built.commandContext });
        } else {
          // Prefer playCueItem from the confirmation modal; default insert if called directly.
          cueCommand.mutate({
            action: 'playMedia',
            entityRef,
            itemId,
            cuePlayMode: 'insert',
            ...built.commandContext,
          });
        }
        return;
      }

      const next = toggleMainContentInSelection(selectedItemIds, itemId);
      updateStagedSelection.mutate({
        entityRef,
        stagedSelection: {
          selectedItemIds: next,
          showMode,
          ...(next.length === 0 ? { clearSelection: true } : {}),
        },
      });
    },
    [
      buildLiveCueCommandContext,
      canInteract,
      cueCommand,
      entityRef,
      getItemPhase,
      performMode,
      selectedItemIds,
      showMode,
      simulationPlayMedia,
      simulationStopMedia,
      updateStagedSelection,
      usesAutoCue,
    ],
  );

  const mediaAgentAvailable = performMode === 'simulation' ? false : cueState?.mediaAgentAvailable;
  const backupSlidesActive = performMode === 'play' && isMediaAgentBackupSlidesActive(liveContextState?.playState);

  const liveSheetRoomKey = entityRoomKeyFromEntityRef(entityRef ?? '');
  const wsCueSheet = usePerformControlWsStore((s) =>
    liveSheetRoomKey ? s.cueSheetByRoom[liveSheetRoomKey] : undefined,
  );
  const liveCueSheet = wsCueSheet ?? liveContextState?.cueSheet;
  const liveSheetRows = useMemo(() => (liveCueSheet ? liveCueSheetToSimulationRows(liveCueSheet) : []), [liveCueSheet]);

  const nextCuePreviewRow = useMemo(() => {
    if (performMode === 'play') {
      return resolveLiveNextCueTarget(cueState, liveContextState?.playState?.lookAheadInsertResume);
    }
    const currentPointer = resolveCurrentCueSheetPointer(simulationRows, performMode, simulationPointer, cueState, {
      simulationOverridePointer,
    });
    return resolveNextCuePreviewRow(simulationRows, currentPointer, {
      cueOverrideActive: simulationOverridePointer != null,
      resumePointer: simulationPointer,
      selectedItemIds: simulationSelectedItemIds,
      flow: flow ?? undefined,
      playedReturnPreviousKeys,
    });
  }, [
    cueState,
    flow,
    liveContextState?.playState?.lookAheadInsertResume,
    performMode,
    playedReturnPreviousKeys,
    simulationOverridePointer,
    simulationPointer,
    simulationRows,
    simulationSelectedItemIds,
  ]);

  const isNextCueItem = useCallback(
    (itemId: string, playlistContext?: SimulationCuePlaylistContext) => {
      if (jumpIndicatorLatch?.expectedNext && !isJumpNextIndicatorReady(jumpIndicatorLatch, cueState)) {
        return false;
      }
      if (!isNextCuePreviewTarget(nextCuePreviewRow, itemId, playlistContext)) {
        return false;
      }
      const nextCueItemId = nextCuePreviewRow?.cueItemId;
      if (!nextCueItemId || playlistContext?.cueItemId) {
        return true;
      }
      const rows = performMode === 'play' && liveSheetRows.length > 0 ? liveSheetRows : simulationRows;
      const index = findSimulationRowIndex(rows, itemId, playlistContext);
      const row = index >= 0 ? rows[index] : undefined;
      if (!row?.cueItemId) {
        return true;
      }
      return row.cueItemId === nextCueItemId;
    },
    [cueState, jumpIndicatorLatch, liveSheetRows, nextCuePreviewRow, performMode, simulationRows],
  );

  const applyNextShowModePreselect = useCallback(
    (
      mode: 'inherit' | 'mediaOnly' | 'both' | 'speakerOnly',
      itemId: string,
      playlistContext?: SimulationCuePlaylistContext,
    ) => {
      if (!isNextCueItem(itemId, playlistContext)) {
        return;
      }
      const predecessor =
        performMode === 'simulation'
          ? simulationOnAirShowMode
          : liveContextState?.playState?.showMode === 'speakerOnly'
            ? ('speakerOnly' as const)
            : liveContextState?.playState?.showMode === 'mediaOnly'
              ? ('mediaOnly' as const)
              : ('both' as const);
      const resolved = mode === 'inherit' ? predecessor : mode;
      if (performMode === 'simulation') {
        // Playlist cycle is an intentional preselect change — allow automatic apply again.
        clearNextShowModePreselectHold();
        setSimulationNextShowMode(resolved);
        return;
      }
      if (!entityRef) {
        return;
      }
      // LIVE: operator control message; BE also refreshes look-ahead from sheet patch.
      updateStagedSelection.mutate({
        entityRef,
        stagedSelection: {
          selectedItemIds,
          showMode: resolved,
          skippedItemIds,
          focusedItemId: liveContextState?.stagedSelection?.focusedItemId,
        },
      });
    },
    [
      clearNextShowModePreselectHold,
      entityRef,
      liveContextState?.playState?.showMode,
      liveContextState?.stagedSelection?.focusedItemId,
      isNextCueItem,
      performMode,
      selectedItemIds,
      setSimulationNextShowMode,
      simulationOnAirShowMode,
      skippedItemIds,
      updateStagedSelection,
    ],
  );

  const value = useMemo(
    () => ({
      entityRef,
      canInteract,
      usesAutoCue,
      mediaAgentAvailable,
      backupSlidesActive,
      isStaged,
      isPlayed,
      getItemPhase,
      isShowingScopeIdle,
      isNextCueItem,
      isSkipped,
      toggleItem,
      advanceNextCue,
      playCueItem,
      setItemSkipped,
      isItemCueCommandPending,
      isLiveCurrentItem,
      applyNextShowModePreselect,
    }),
    [
      entityRef,
      canInteract,
      usesAutoCue,
      mediaAgentAvailable,
      backupSlidesActive,
      isStaged,
      isPlayed,
      getItemPhase,
      isShowingScopeIdle,
      isNextCueItem,
      isSkipped,
      toggleItem,
      advanceNextCue,
      playCueItem,
      setItemSkipped,
      isItemCueCommandPending,
      isLiveCurrentItem,
      applyNextShowModePreselect,
    ],
  );

  return <FlowControlStagingContext.Provider value={value}>{children}</FlowControlStagingContext.Provider>;
}

export function useFlowControlStaging(): FlowControlStagingContextValue | null {
  return useContext(FlowControlStagingContext);
}
